// src/components/Transactions/NonCategorized.js - Atualizado para suportar colaboradores
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import MainLayout from "../Layout/MainLayout";
import TransactionItem from "./TransactionItem";
import SuggestionItem from "./SuggestionItem";
import "./Transactions.css";

// Importar função de limpeza
import { cleanTransactionName, getCategorizationKey } from "../../utils/cleanTransactionName";

// Importar serviços refatorados
import { 
  loadAvailablePeriods, 
  loadUserCategories, 
  loadCategoryMappings, 
  loadTransactionsForPeriod, 
  saveCategoryMappings 
} from "../../services/transactionRepository";

import {
  checkTransactionCategorization,
  findUnsavedAutoMappedTransactions,
  groupTransactionsByPattern,
  prepareUpdatedMappings
} from "../../services/categorizationService";

import { getSuggestionsForTransaction, getMappingStats } from "../../services/categorySuggestionsService";

const NonCategorized = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categories, setCategories] = useState({});
  const [isCategorizing, setIsCategorizing] = useState(true);
  const [categoryMappings, setCategoryMappings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [autoMappedTransactions, setAutoMappedTransactions] = useState([]);
  const [processingAutoMap, setProcessingAutoMap] = useState(false);
  const [businessModel, setBusinessModel] = useState("");
  const [activeTab, setActiveTab] = useState("to-categorize"); // "to-categorize" ou "suggestions"
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [fadingOutSuggestions, setFadingOutSuggestions] = useState({});
  const [isCollaborator, setIsCollaborator] = useState(false); // Estado para indicar se é colaborador
  const [workspaceOwner, setWorkspaceOwner] = useState(null); // ID do proprietário do workspace
  const transactionsPerPage = 10;

  // Carregar dados iniciais (usuário, períodos, categorias)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        if (auth.currentUser) {
          setUser(auth.currentUser);
          
          // Verificar se o usuário é um colaborador
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const isUserCollaborator = userData.isCollaborator === true;
            setIsCollaborator(isUserCollaborator);
            
            if (isUserCollaborator) {
              // Verificar informações do workspace
              const invitedBy = userData.invitedBy;
              const originalInvitation = userData.originalInvitation || {};
              const profileId = originalInvitation.profileId;
              
              // Verificar se está acessando o workspace para o qual foi convidado
              if (profileId && invitedBy && localStorage.getItem('active_business_profile_id') === profileId) {
                setWorkspaceOwner(invitedBy);
                console.log(`Colaborador detectado. Proprietário do workspace: ${invitedBy}`);
                
                // Carregar o modelo de negócio do workspace do proprietário
                try {
                  const ownerCategoriesDoc = await getDoc(doc(db, "userCategories", invitedBy, profileId, "categories"));
                  if (ownerCategoriesDoc.exists() && ownerCategoriesDoc.data().businessModel) {
                    const model = ownerCategoriesDoc.data().businessModel;
                    setBusinessModel(model);
                    console.log("Modelo de negócio do workspace carregado:", model);
                  } else {
                    // Tentar caminho legado
                    const legacyDoc = await getDoc(doc(db, "userCategories", invitedBy));
                    if (legacyDoc.exists() && legacyDoc.data().businessModel) {
                      setBusinessModel(legacyDoc.data().businessModel);
                    }
                  }
                } catch (err) {
                  console.error("Erro ao carregar modelo de negócio do workspace:", err);
                }
              }
            } else {
              // Para usuários regulares, carregar modelo de negócio normal
              try {
                const userCategoriesDoc = await getDoc(doc(db, "userCategories", auth.currentUser.uid));
                if (userCategoriesDoc.exists() && userCategoriesDoc.data().businessModel) {
                  const model = userCategoriesDoc.data().businessModel;
                  setBusinessModel(model);
                  console.log("Modelo de negócio carregado:", model);
                }
              } catch (err) {
                console.error("Erro ao carregar modelo de negócio:", err);
              }
            }
          }
          
          // Carregar períodos disponíveis usando o serviço refatorado (já lida com colaboradores)
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);
          
          // Verificar se existe um período salvo no localStorage
          let savedPeriod = localStorage.getItem('selectedPeriod');
          let periodToUse;
          
          if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
            periodToUse = savedPeriod;
          } else if (availablePeriods.length > 0) {
            periodToUse = availablePeriods[0].value;
            localStorage.setItem('selectedPeriod', periodToUse);
          }
          
          if (periodToUse) {
            setSelectedPeriod(periodToUse);
          }
          
          // Carregar categorias do usuário usando o serviço refatorado (já lida com colaboradores)
          const userCategories = await loadUserCategories();
          
          console.log("Categorias carregadas:", userCategories);
          setCategories(userCategories);
          
          // Carregar mapeamentos de categorias usando o serviço refatorado (já lida com colaboradores)
          const mappings = await loadCategoryMappings();
          setCategoryMappings(mappings);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        setError("Não foi possível carregar os dados iniciais.");
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [businessModel]);

  // Efeito para carregar transações quando mudar período
  useEffect(() => {
    if (selectedPeriod) {
      loadNonCategorizedTransactions();
    }
  }, [selectedPeriod, categoryMappings]);

  // Efeito para processar transações auto-mapeadas - VERSÃO CORRIGIDA
  useEffect(() => {
    if (autoMappedTransactions.length > 0 && !loading && !processingAutoMap) {
      setProcessingAutoMap(true);
      
      // Verificar transações que precisam ser automaticamente categorizadas
      const unsavedTransactions = findUnsavedAutoMappedTransactions(
        autoMappedTransactions, 
        categoryMappings
      );
      
      // Se há transações não salvas, processá-las e depois LIMPAR o array
      if (unsavedTransactions.length > 0) {
        saveAutoMappedTransactions(unsavedTransactions)
          .then(() => {
            setAutoMappedTransactions([]);
            setProcessingAutoMap(false);
          })
          .catch(error => {
            console.error("Erro ao salvar auto-mapeamentos:", error);
            setProcessingAutoMap(false);
          });
      } else {
        setAutoMappedTransactions([]);
        setProcessingAutoMap(false);
      }
    }
  }, [autoMappedTransactions, categoryMappings, loading, processingAutoMap]);

  // Efeito para filtrar transações quando mudar a busca ou filtro
  useEffect(() => {
    if (transactions.length > 0) {
      filterTransactions(true);
    }
  }, [searchTerm, filterValue]);

  // Novo efeito para filtrar transações quando elas mudarem (sem resetar a página)
  useEffect(() => {
    if (transactions.length > 0) {
      filterTransactions(false);
    }
  }, [transactions]);

  // Função para filtrar transações - MODIFICADA para aceitar um parâmetro de reset
  const filterTransactions = (resetPage = false) => {
    let filtered = [...transactions];
    
    // Aplicar filtro de busca por texto - ATUALIZADO para usar nome limpo
    if (searchTerm) {
      filtered = filtered.filter(transaction => {
        const cleanedName = cleanTransactionName(transaction.description, transaction.bankInfo?.org || '');
        return cleanedName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    // Aplicar filtro de valores positivos/negativos
    if (filterValue !== "all") {
      filtered = filtered.filter(transaction => {
        if (filterValue === "positive") {
          return transaction.amount >= 0;
        } else if (filterValue === "negative") {
          return transaction.amount < 0;
        }
        return true;
      });
    }
    
    setFilteredTransactions(filtered);
    
    // Calcular o novo total de páginas
    const newTotalPages = Math.max(1, Math.ceil(filtered.length / transactionsPerPage));
    setTotalPages(newTotalPages);
    
    // Apenas resetar a página se solicitado ou se a página atual for maior que o total de páginas
    if (resetPage || currentPage > newTotalPages) {
      setCurrentPage(1);
    }
  };

  // Salvar categorizações automáticas no Firestore - VERSÃO CORRIGIDA
  const saveAutoMappedTransactions = async (transactions = []) => {
    try {
      if (!auth.currentUser) return Promise.resolve();
      
      const transactionsToProcess = transactions.length > 0 ? 
        transactions : autoMappedTransactions;
      
      if (transactionsToProcess.length === 0) {
        setProcessingAutoMap(false);
        return Promise.resolve();
      }
      
      setLoading(true);
      console.log("Processando categorizações automáticas:", transactionsToProcess.length);
      
      // Agrupar transações por padrões
      const patternGroups = groupTransactionsByPattern(transactionsToProcess);
      
      // Preparar mapeamentos atualizados
      const updatedMappings = prepareUpdatedMappings(categoryMappings, patternGroups);
      
      // Salvar no Firestore usando o serviço refatorado (já lida com colaboradores)
      await saveCategoryMappings(updatedMappings);
      
      // Update the local state
      setCategoryMappings(updatedMappings);
      
      // IMPORTANTE: Limpar explicitamente o array de transações auto-mapeadas
      setAutoMappedTransactions([]);
      
      // Show success message
      setSuccess(`${transactionsToProcess.length} transações categorizadas automaticamente e salvas.`);
      setTimeout(() => setSuccess(""), 3000);
      
      // Wait a bit before reloading to prevent immediate re-processing
      setTimeout(() => {
        loadNonCategorizedTransactions();
      }, 1000);
      
      return Promise.resolve();
    } catch (error) {
      console.error("Erro ao salvar categorizações automáticas:", error);
      setError(`Não foi possível salvar as categorizações automáticas: ${error.message}`);
      return Promise.reject(error);
    } finally {
      setLoading(false);
      setProcessingAutoMap(false);
    }
  };

  // Carregar transações não categorizadas
  const loadNonCategorizedTransactions = async () => {
    try {
      setLoading(true);
      setAutoMappedTransactions([]);
      
      if (!auth.currentUser || !selectedPeriod) return;
      
      // Carregar transações para o período selecionado usando o serviço refatorado (já lida com colaboradores)
      const { transactions: allTransactions, autoMappedToSave } = 
        await loadTransactionsForPeriod(selectedPeriod);
      
      // Filtrar transações não categorizadas
      const nonCategorizedTransactions = allTransactions.filter(transaction => {
        // Verificar se a transação está categorizada
        const result = checkTransactionCategorization(transaction, categoryMappings);
        return !result.isCategorized;
      });
      
      // Adicionar transações auto-mapeadas para processamento
      if (autoMappedToSave.length > 0 && !processingAutoMap) {
        console.log(`Encontradas ${autoMappedToSave.length} transações com categorização automática não salva.`);
        setAutoMappedTransactions(autoMappedToSave);
      }
      
      console.log(`Total de transações não categorizadas: ${nonCategorizedTransactions.length}`);
      
      setTransactions(nonCategorizedTransactions);
      setFilteredTransactions(nonCategorizedTransactions);
      
      // Calcular total de páginas
      setTotalPages(Math.max(1, Math.ceil(nonCategorizedTransactions.length / transactionsPerPage)));
      setCurrentPage(1); // Resetar para a primeira página
    } catch (error) {
      console.error("Erro ao carregar transações não categorizadas:", error);
      setError("Não foi possível carregar as transações não categorizadas.");
    } finally {
      setLoading(false);
    }
  };

  // Função para alterar o período
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    
    // Salvar o período selecionado no localStorage
    localStorage.setItem('selectedPeriod', period);
  };

  // Obter transações para a página atual
  const getCurrentTransactions = () => {
    const startIndex = (currentPage - 1) * transactionsPerPage;
    const endIndex = startIndex + transactionsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  };

  // Função para mudar a página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Função para categorizar uma transação (apenas localmente)
  // MODIFICADA para usar a chave de categorização baseada no nome limpo
  const handleCategoryChange = (transactionId, group, item, categoryPath, fromSuggestion = false) => {
    try {
      setError("");
      
      // Encontrar a transação
      const transaction = transactions.find(t => t.id === transactionId);
      if (!transaction) return;
      
      // Atualizar a transação na interface (apenas localmente)
      setTransactions(prevTransactions => 
        prevTransactions.map(t => {
          if (t.id === transactionId) {
            return {
              ...t,
              category: item,
              categoryPath: categoryPath,
              groupName: group,
              modified: true,
              pendingSave: true
            };
          }
          return t;
        })
      );
      
      // Exibir mensagem para lembrar o usuário de salvar apenas se não for de uma sugestão
      if (!fromSuggestion) {
        setSuccess(`Transação "${transaction.description}" categorizada. Não esqueça de salvar todas as alterações!`);
      }
      
    } catch (error) {
      console.error("Erro ao categorizar transação:", error);
      setError("Não foi possível categorizar a transação. Tente novamente.");
    }
  };

  // Salvar todas as categorizações
  const saveAllMappings = async () => {
    try {
      setLoading(true);
      setError("");
      
      const pendingTransactions = transactions.filter(t => t.pendingSave);
      if (pendingTransactions.length === 0) {
        setError("Nenhuma transação pendente para salvar.");
        setLoading(false);
        return;
      }
      
      // Agrupar transações por padrões
      const patternGroups = groupTransactionsByPattern(pendingTransactions);
      
      // Preparar mapeamentos atualizados
      const updatedMappings = prepareUpdatedMappings(categoryMappings, patternGroups);
      
      // Salvar no Firestore usando o serviço refatorado (já lida com colaboradores)
      await saveCategoryMappings(updatedMappings);
      
      // Atualizar o estado local
      setCategoryMappings(updatedMappings);
      
      // Remover o estado de pendente das transações
      setTransactions(prevTransactions => 
        prevTransactions.map(t => ({
          ...t,
          pendingSave: false
        }))
      );
      
      // Recarregar transações
      await loadNonCategorizedTransactions();
      
      // Exibir mensagem de sucesso
      setSuccess(`${pendingTransactions.length} transações categorizadas e salvas com sucesso!`);
      
      // Limpar mensagem após 3 segundos apenas para a mensagem de salvamento
      setTimeout(() => {
        setSuccess("");
      }, 3000);
      
    } catch (error) {
      console.error("Erro ao salvar mapeamentos:", error);
      setError("Não foi possível salvar os mapeamentos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Nova função para carregar sugestões
  const loadSuggestions = () => {
    try {
      setLoadingSuggestions(true);
      
      if (!filteredTransactions.length) {
        setSuggestions([]);
        return;
      }
      
      // Agrupar transações por descrição normalizada (usando nome limpo)
      const transactionGroups = {};
      
      filteredTransactions.forEach(transaction => {
        const normalizedDesc = getCategorizationKey(transaction.description, transaction.bankInfo?.org || '');
        
        if (!transactionGroups[normalizedDesc]) {
          transactionGroups[normalizedDesc] = [];
        }
        
        transactionGroups[normalizedDesc].push(transaction);
      });
      
      const potentialSuggestions = [];
      
      // Para cada grupo de transações iguais
      Object.values(transactionGroups).forEach(transactionGroup => {
        // Ordenar por data decrescente (mais recentes primeiro)
        transactionGroup.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // Pegar apenas a transação mais recente
        const mostRecentTransaction = transactionGroup[0];
        
        // Buscar sugestões para esta transação
        const suggestions = getSuggestionsForTransaction(mostRecentTransaction);
        
        // Adicionar as melhores sugestões (score > 0.6)
        suggestions.forEach(suggestion => {
          if (suggestion.score > 0.6) {
            potentialSuggestions.push({
              transaction: mostRecentTransaction,
              suggestion,
              confidence: suggestion.score,
              matchType: suggestion.matchType,
              cleanedName: cleanTransactionName(mostRecentTransaction.description, mostRecentTransaction.bankInfo?.org || '')
            });
          }
        });
      });
      
      // Ordenar sugestões por confiança
      potentialSuggestions.sort((a, b) => b.confidence - a.confidence);
      
      setSuggestions(potentialSuggestions);
    } catch (error) {
      console.error("Erro ao carregar sugestões:", error);
      setError("Não foi possível carregar as sugestões.");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Função para aceitar uma sugestão - MODIFICADA para adicionar fade-out visual
  const acceptSuggestion = async (transaction, suggestion) => {
    try {
      // Marcar transação para fade-out aceito
      setFadingOutSuggestions(prev => ({
        ...prev,
        [transaction.id]: 'accepted'
      }));
      
      // MODIFICADO: Aguarda um pouco para a animação começar
      await new Promise(resolve => setTimeout(resolve, 50)); // Reduzido de 100 para 50
      
      // Categorizar a transação com a sugestão
      handleCategoryChange(
        transaction.id, 
        suggestion.groupName, 
        suggestion.category, 
        suggestion.categoryPath,
        true // fromSuggestion = true
      );
      
      // Salvar imediatamente no Firebase
      setLoading(true);
      
      // Agrupar apenas esta transação
      const transactionToSave = {
        ...transaction,
        category: suggestion.category,
        categoryPath: suggestion.categoryPath,
        groupName: suggestion.groupName,
        pendingSave: true
      };
      
      const patternGroups = groupTransactionsByPattern([transactionToSave]);
      const updatedMappings = prepareUpdatedMappings(categoryMappings, patternGroups);
      
      // Salvar no Firebase usando o serviço refatorado (já lida com colaboradores)
      await saveCategoryMappings(updatedMappings);
      
      // Atualizar o estado local
      setCategoryMappings(updatedMappings);
      
      // Atualizar a transação no estado local
      setTransactions(prevTransactions => 
        prevTransactions.map(t => {
          if (t.id === transaction.id) {
            return {
              ...t,
              category: suggestion.category,
              categoryPath: suggestion.categoryPath,
              groupName: suggestion.groupName,
              modified: true,
              pendingSave: false
            };
          }
          return t;
        })
      );
      
      // MODIFICADO: Aguarda o fade-out terminar antes de remover a sugestão
      setTimeout(() => {
        setSuggestions(prev => prev.filter(s => s.transaction.id !== transaction.id));
        setFadingOutSuggestions(prev => {
          const newState = { ...prev };
          delete newState[transaction.id];
          return newState;
        });
      }, 700); // Reduzido de 1000 para 700 para corresponder à animação
      
    } catch (error) {
      console.error("Erro ao salvar sugestão:", error);
      
      // Reverter o fade-out em caso de erro
      setFadingOutSuggestions(prev => {
        const newState = { ...prev };
        delete newState[transaction.id];
        return newState;
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para ignorar uma sugestão - MODIFICADA para adicionar fade-out visual
  const ignoreSuggestion = (transaction) => {
    // Marcar transação para fade-out negado
    setFadingOutSuggestions(prev => ({
      ...prev,
      [transaction.id]: 'rejected'
    }));
    
    // MODIFICADO: Aguarda o fade-out terminar antes de remover a sugestão
    setTimeout(() => {
      setSuggestions(prev => prev.filter(s => s.transaction.id !== transaction.id));
      setFadingOutSuggestions(prev => {
        const newState = { ...prev };
        delete newState[transaction.id];
        return newState;
      });
    }, 700); // Reduzido de 1000 para 700
  };

  // Efeito para carregar sugestões quando mudar a aba
  useEffect(() => {
    if (activeTab === "suggestions" && filteredTransactions.length > 0) {
      loadSuggestions();
    }
  }, [activeTab, filteredTransactions]);

  // Log de estatísticas das sugestões disponíveis
  useEffect(() => {
    const stats = getMappingStats();
    console.log('Estatísticas de mapeamentos:', stats);
  }, []);

  if (loading && (!selectedPeriod || Object.keys(categories).length === 0)) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="non-categorized-container">
        {/* Seletor de período no padrão do Dashboard */}
        <div className="period-selector-container">
          <div className="period-selector-header">
            <h2>
              Transações Não Categorizadas
              {isCollaborator && (
                <span className="collaborator-indicator"> (Modo Colaborador)</span>
              )}
            </h2>
            <div className="period-dropdown">
              <label htmlFor="period-select">Período: </label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={handlePeriodChange}
                disabled={loading}
              >
                {periods.length === 0 ? (
                  <option value="">Nenhum período disponível</option>
                ) : (
                  periods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            {success}
          </div>
        )}
        
        {/* REMOVIDO: autoMappedTransactions.length > 0 notification */}
        
        {/* Abas para alternar entre Não Categorizadas e Sugestões */}
        <div className="tabs-container">
          <div className="tabs-header">
            <button 
              className={`tab-button ${activeTab === 'to-categorize' ? 'active' : ''}`}
              onClick={() => setActiveTab('to-categorize')}
            >
              A Categorizar ({filteredTransactions.length})
            </button>
            <button 
              className={`tab-button ${activeTab === 'suggestions' ? 'active' : ''}`}
              onClick={() => setActiveTab('suggestions')}
            >
              Sugestões {suggestions.length > 0 ? `(${suggestions.length})` : ''}
            </button>
          </div>
        </div>
        
        {/* Conteúdo da aba Não Categorizadas */}
        {activeTab === 'to-categorize' && (
          <>
            {/* Barra de pesquisa com filtro por valores positivos/negativos */}
            <div className="transactions-filters">
              <div className="search-filter">
                <input
                  type="text"
                  placeholder="Buscar por nome real da transação..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
                
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Todos os valores</option>
                  <option value="positive">Valores positivos</option>
                  <option value="negative">Valores negativos</option>
                </select>
              </div>
              
              <div className="transactions-count">
                {filteredTransactions.length} transações não categorizadas encontradas
              </div>
            </div>
            
            {loading ? (
              <div className="loading-data">
                <div className="loading-spinner"></div>
                <p>Carregando transações...</p>
              </div>
            ) : (
              <>
                {filteredTransactions.length > 0 ? (
                  <div className="transactions-content">
                    <div className="transactions-list">
                      {getCurrentTransactions().map((transaction) => (
                        <TransactionItem
                          key={transaction.id}
                          transaction={transaction}
                          categories={categories}
                          onCategoryChange={handleCategoryChange}
                          isCategorizing={isCategorizing}
                          businessModel={businessModel}
                          cleanedName={cleanTransactionName(transaction.description, transaction.bankInfo?.org || '')}
                        />
                      ))}
                    </div>
                    
                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="pagination-button"
                        >
                          &laquo;
                        </button>
                        
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="pagination-button"
                        >
                          &lt;
                        </button>
                        
                        <span className="pagination-info">
                          Página {currentPage} de {totalPages}
                        </span>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="pagination-button"
                        >
                          &gt;
                        </button>
                        
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          className="pagination-button"
                        >
                          &raquo;
                        </button>
                      </div>
                    )}
                    
                    {/* Botão para salvar todos os mapeamentos */}
                    <div className="save-mappings">
                      <button
                        className="save-all-button"
                        onClick={saveAllMappings}
                        disabled={loading || !transactions.some(t => t.pendingSave)}
                      >
                        {loading ? "Salvando..." : "Salvar Todas as Categorizações"}
                      </button>
                      
                      {transactions.some(t => t.pendingSave) && (
                        <div className="pending-changes-notification">
                          Existem alterações não salvas! Clique em "Salvar Todas as Categorizações" para confirmar.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="no-transactions">
                    <p>Não existem transações não categorizadas para este período.</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {/* Conteúdo da aba Sugestões */}
        {activeTab === 'suggestions' && (
          <div className="suggestions-content">
            {loadingSuggestions ? (
              <div className="loading-data">
                <div className="loading-spinner"></div>
                <p>Carregando sugestões...</p>
              </div>
            ) : suggestions.length > 0 ? (
              <div className="suggestions-list">
                {suggestions.map((suggestionData, index) => {
                  // Determinar classe de fade-out
                  const fadeOutClass = fadingOutSuggestions[suggestionData.transaction.id] 
                    ? `${fadingOutSuggestions[suggestionData.transaction.id]}-fade-out`
                    : '';
                  
                  return (
                    <div 
                      key={suggestionData.transaction.id}
                      className={`suggestion-item ${fadeOutClass}`}
                    >
                      <SuggestionItem
                        transaction={suggestionData.transaction}
                        suggestion={suggestionData.suggestion}
                        confidence={suggestionData.confidence}
                        matchType={suggestionData.matchType}
                        onAccept={() => acceptSuggestion(suggestionData.transaction, suggestionData.suggestion)}
                        onIgnore={() => ignoreSuggestion(suggestionData.transaction)}
                        cleanedName={suggestionData.cleanedName}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-suggestions">
                <p>Nenhuma sugestão disponível para as transações atuais.</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Adicionar estilo CSS para indicador de colaborador */}
      <style>{`
        .collaborator-indicator {
          color: #0066cc;
          font-size: 0.8em;
          font-weight: normal;
          margin-left: 8px;
        }
      `}</style>
    </MainLayout>
  );
};

export default NonCategorized;